<template>
  <main class="learn-index-page">
    <header class="learn-index-header">
      <p class="learn-index-kicker">{{ labels.kicker }}</p>
      <h1>{{ labels.title }}</h1>
      <p>{{ labels.description }}</p>
      <div class="learn-index-actions">
        <router-link to="/" class="index-action index-action-primary">
          {{ labels.home }}
        </router-link>
        <router-link :to="alternatePath" class="index-action index-action-secondary">
          {{ labels.alternate }}
        </router-link>
      </div>
    </header>

    <section class="guide-list" :aria-label="labels.listLabel">
      <router-link
        v-for="article in articles"
        :key="article.slug"
        :to="`${basePath}/${article.slug}`"
        class="guide-item"
      >
        <div>
          <h2>{{ article.title }}</h2>
          <p>{{ article.description }}</p>
        </div>
        <span class="material-icons" aria-hidden="true">arrow_forward</span>
      </router-link>
    </section>
  </main>
</template>

<script>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { learnArticles, zhLearnArticles } from '@/assets/seo/learnArticles'

export default {
  name: 'LearnIndex',
  setup() {
    const route = useRoute()
    const isChinese = computed(() => route.name === 'ZhLearnIndex')
    const articles = computed(() => (isChinese.value ? zhLearnArticles : learnArticles))
    const basePath = computed(() => (isChinese.value ? '/zh/learn' : '/learn'))
    const alternatePath = computed(() => (isChinese.value ? '/learn' : '/zh/learn'))
    const labels = computed(() => (
      isChinese.value
        ? {
            kicker: 'GoodScriber 中文学习指南',
            title: '英语听力、口语与跟读学习指南',
            description: '围绕精听、逐句跟读、英语面试、雅思备考和播客学习，找到可以直接照着练的具体方法。',
            home: '返回 GoodScriber',
            alternate: 'View English guides',
            listLabel: '中文学习指南列表'
          }
        : {
            kicker: 'GoodScriber learning guides',
            title: 'Listening, speaking and shadowing guides',
            description: 'Practical workflows for shadowing, listening practice, IELTS speaking, podcasts, subtitles, and learning with your own audio.',
            home: 'Back to GoodScriber',
            alternate: '查看中文指南',
            listLabel: 'English learning guide list'
          }
    ))

    return {
      articles,
      basePath,
      alternatePath,
      labels
    }
  }
}
</script>

<style scoped>
.learn-index-page {
  width: min(1040px, calc(100% - 48px));
  min-height: 100vh;
  margin: 0 auto;
  padding: calc(var(--navbar-height) + 56px) 0 88px;
  color: var(--color-text);
}

.learn-index-header {
  max-width: 760px;
}

.learn-index-kicker {
  margin: 0 0 12px;
  color: var(--workspace-accent-dark);
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.learn-index-header h1 {
  margin: 0;
  font-size: clamp(2rem, 5vw, 3.2rem);
  line-height: 1.08;
  font-weight: 840;
}

.learn-index-header > p:not(.learn-index-kicker) {
  margin: 18px 0 0;
  color: var(--color-text-muted);
  font-size: 1.06rem;
  line-height: 1.72;
}

.learn-index-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
}

.index-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 0 18px;
  border-radius: 999px;
  font-weight: 760;
  text-decoration: none;
}

.index-action-primary {
  color: #ffffff;
  background: #111827;
}

.index-action-secondary {
  color: var(--workspace-accent-dark);
  background: rgba(255, 255, 255, 0.62);
  border: 1px solid rgba(23, 107, 77, 0.18);
}

.guide-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 48px;
}

.guide-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 18px;
  min-height: 164px;
  padding: 22px;
  color: inherit;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(23, 107, 77, 0.14);
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
  text-decoration: none;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.guide-item:hover {
  transform: translateY(-2px);
  border-color: rgba(23, 107, 77, 0.3);
  box-shadow: var(--shadow-md);
}

.guide-item h2 {
  margin: 0;
  font-size: 1.12rem;
  line-height: 1.4;
}

.guide-item p {
  margin: 10px 0 0;
  color: var(--color-text-muted);
  font-size: 0.94rem;
  line-height: 1.65;
}

.guide-item .material-icons {
  color: var(--workspace-accent-dark);
  font-size: 20px;
}

@media (max-width: 760px) {
  .learn-index-page {
    width: min(100% - 32px, 1040px);
    padding-top: calc(var(--navbar-height) + 32px);
    padding-bottom: 64px;
  }

  .guide-list {
    grid-template-columns: 1fr;
    margin-top: 36px;
  }

  .guide-item {
    min-height: 0;
    padding: 18px;
  }
}
</style>
